import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { map, catchError, tap } from "rxjs/operators";

// Env
import { environment } from "src/environments/environment";

// Utils
import { constants } from "../utils/constants";
import { api } from "../utils/api";
import helpers from "../utils/helpers";

// Model
import { User } from "../models/user.model";
import { Role } from "../models/role.model";
import { UserLogged } from "../models/user-logged.model";

@Injectable({
  providedIn: "any",
})
export class UserService {
  // API
  private USERS_URL = environment.API_URL + api.users.getUsers;
  private ROLES_URL = environment.API_URL + api.users.getRoles;
  private INSERT_URL = environment.API_URL + api.users.insert;
  private UPDATE_URL = environment.API_URL + api.users.update;
  private DELETE_URL = environment.API_URL + api.users.delete;
  private EMAIL_URL = environment.API_URL + api.users.email;

  private emailTemplates = {
    created: "BFPE_EXTRANET_ALTA_USUARIO",
    updated: "BFPE_EXTRANET_MODIFICACION_USUARIO",
  };

  users: User[] = [];
  user: User;
  roles: Role[] = [];

  dirty: boolean = true;

  constructor(private httpClient: HttpClient) {}

  refresh() {
    this.dirty = true;
  }

  resetUsers() {
    this.users = [];
  }

  // =================================================================
  //                            CRUD
  // =================================================================
  /**
   * Invoca el servicio de consulta masiva de usuarios de Extranet
   */
  public getUsers(companyId: string): Observable<User[]> {
    if (!this.dirty && this.users.length > 0) {
      return of(this.users);
    }
    this.dirty = false;

    const body = this.getMassiveSelectBody(companyId);

    return this.httpClient.post(this.USERS_URL, body).pipe(
      map((resp: any) => {
        if (resp.Detail) throw constants.alerts.noResults.unexpected;
        if (helpers.emptyResponse(resp))
          throw constants.alerts.noResults.noUsers;
        //Ordenar por fecha de creacion
        const userList = this.sortByDate(resp.collection.systemUser);
        //Procesar lista ordenada
        this.users = userList.map((item: any) => {
          return new User({
            id: item.userId,
            firstName: item.customer?.firstName1,
            lastName: item.customer?.lastName1,
            docNumber: item.customer?.identificationNumber,
            docType: item.customer?.identificationType?.mnemonic,
            email: item.electronicsContact?.emailAddressComplete,
            phone: item.contactCelPhone?.phoneNumber,
            charge: item.userPosition?.description,
            roles: helpers.processRoles(item?.roleTypes),
          });
        });
        return this.users;
      }),
      catchError((err) => {
        if (err.type) {
          throw err;
        }
        throw constants.alerts.noResults.unexpected;
      })
    );
  }

  /**
   * Invoca el servicio de inserción de un nuevo usuario de Extranet
   */
  public insert(user: User, userLogged: UserLogged): Observable<any> {
    this.user = user;
    const body = this.getInsertBody(user, userLogged.getUserData().companyId);
    return this.httpClient.post(this.INSERT_URL, body).pipe(
      tap((resp: any) => {
        if (resp.Detail) throw resp.Detail?.errors?.error;
        this.user.id = resp.systemUser?.userId;
        this.refresh();
        this.sendEmail(user, userLogged, this.emailTemplates.created);
      }),
      catchError((err) => {
        if (err.sourceCode === "OPERADOR_EMPRESA_EXISTENTE") {
          throw constants.alerts.existingUser;
        }
        throw constants.alerts.default;
      })
    );
  }

  /**
   * Invoca el servicio de modificación de un usuario de Extranet
   */
  public update(user: User, userLogged: UserLogged): Observable<any> {
    this.user = user;
    const body = this.getModifyBody(user, userLogged.getUserData().companyId);
    return this.httpClient.post(this.UPDATE_URL, body).pipe(
      tap((resp: any) => {
        if (resp.Detail) throw Error();
        this.refresh();
        this.sendEmail(user, userLogged, this.emailTemplates.updated);
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  /**
   * Invoca el servicio de eliminación de usuario de Extranet
   */
  public delete(userId: string) {
    return this.httpClient.post(this.DELETE_URL, { userId }).pipe(
      tap((resp: any) => {
        if (resp.Detail) throw Error();
        this.refresh();
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  /**
   * Realiza búsqueda por nombre y/o apellido en los usuarios cargados en memoria
   */
  public search(term: string): Observable<User[]> {
    return of(
      this.users.filter((user) => {
        if (
          user.firstName
            .concat(user.lastName)
            .replace(" ", "")
            .toLowerCase()
            .indexOf(term.replace(" ", "").toLowerCase()) >= 0
        )
          return user;
      })
    );
  }

  /**
   * Invoca el servicio de consulta de roles para la inserción y/o modificación de usuarios
   */
  public getRoles(): Observable<Role[]> {
    if (this.roles.length > 0) {
      return of(this.roles);
    }
    const body = {
      shortDesc: "",
      systemUserType: "INSTITUTION_OPERATOR",
    };
    return this.httpClient.post(this.ROLES_URL, body).pipe(
      map((resp: any) => {
        if (resp.Detail) {
          throw new Error();
        }
        this.roles = resp.collection.roleType.map((role: any) => {
          return {
            uri: role.roleTypeId,
            id: parseInt(role.roleTypeId.split("@")[1]),
            name: role.shortDesc,
            fullName: role.longDesc,
          };
        });
        return this.roles;
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  private sendEmail(user: User, userLogged: UserLogged, templateId: string) {
    const body = this.getEmailBody(user, userLogged, templateId);
    this.httpClient.post(this.EMAIL_URL, body).subscribe();
  }

  // =================================================================
  //                            HELPERS
  // =================================================================
  /**
   * Helper para obtener authenticationAlias dado un usuario a crear
   * Ejemplo: 4-21006452
   */
  private getAuthenticationAlias(user: User): string {
    const intValue = user.docType === "DNI" ? 1 : 4;
    return `${intValue}-${user.docNumber}`;
  }

  /**
   * Helper para obtener el body necesario para la inserción de usuario
   */
  private getInsertBody(user: User, companyId: string) {
    const listRoleTypes = user.roles.map((role) => {
      return {
        roleTypeId: role.uri,
      };
    });
    return {
      authenticationAlias: this.getAuthenticationAlias(user),
      authenticationSchemaId: "uriTech=METODO_AUTENTICACION@148",
      channel: "EX",
      contactCelPhone: "519" + user.phone,
      firstName: user.firstName,
      lastName: user.lastName,
      identificationNumber: user.docNumber,
      identificationType: user.docType,
      residenceCode: "PER",
      electronicsContact: user.email,
      userPosition: user.charge,
      intitutionId: companyId,
      listProfiles: [
        {
          profileId: "",
        },
      ],
      listRoleTypes,
    };
  }

  /**
   * Helper para obtener el body necesario para la consulta masiva de usuarios
   */
  private getMassiveSelectBody(companyId: string) {
    return {
      dateFrom: "",
      dateTo: "",
      intitutionId: companyId,
      administrator: "",
      firstName: "",
      identificationNumber: "",
      identificationType: "",
      lastName: "",
      residenceCode: "",
      metaStatus: "",
      status: "",
    };
  }

  /**
   * Helper para obtener el body necesario para la modificación de usuario
   */
  private getModifyBody(user: User, companyId: string) {
    const listRoleTypes = user.roles.map((role) => {
      return {
        roleTypeId: role.uri,
      };
    });
    return {
      contactCelPhone: "519" + user.phone,
      electronicsContact: user.email,
      userId: user.id,
      userPosition: user.charge,
      listRoleTypes,
    };
  }

  /**
   * Helper para obtener el body necesario para enviar la notificación por mail al usuario
   */
  private getEmailBody(user: User, userLogged: UserLogged, templateId: string) {
    return {
      firstNameSource: user.firstName+" "+user.lastName,
      lastNameSource: user.lastName,
      identificationNumberSource: user.docNumber,
      identificationTypeSource: user.docType,
      emailAddressComplete: user.email,
      firstNameTarget: userLogged.getUserData().companyName,
      identificationNumberTarget: userLogged.getUserData().idNumber,
      identificationTypeTarget: userLogged.getUserData().idType,
      executionDate: helpers.internalFormatFromDate(new Date()),
      templateId,
    };
  }

  /**
   * Helper para obtener usuarios ordenados por fecha de creación
   */
  private sortByDate(users): Array<any> {
    try {
      return users.sort((a, b) => {
        return (
          new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
        );
      });
    } catch (err) {
      // Un usuario solo, viene como objeto y no como array
      const userArray = [];
      userArray.push(users);
      return userArray;
    }
  }
}
